import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableStructureAvailability from '../printable/PrintableStructureAvailability';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { format as formatDateFns, parseISO } from 'date-fns';
import { useSettings } from '../../contexts/SettingsContext'; // Import useSettings

// Interface for Structure data (should match the one in PrintableStructureAvailability)
interface Structure {
  id: number;
  code: string;
  type: string;
  name: string;
  address: string;
  size: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  imageUrl?: string; // Added for structure image
}

const StructureAvailabilityPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { settings } = useSettings(); // Get settings from context

  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const typeParam = searchParams.get('type') || 'all';
  const searchTermParam = searchParams.get('searchTerm') || '';
  const structureIdsParam = searchParams.get('structure_ids');

  const dateRange: [Date | null, Date | null] = [
    startDateParam ? parseISO(startDateParam) : null,
    endDateParam ? parseISO(endDateParam) : null,
  ];

  useEffect(() => {
    document.body.classList.add('print-mode'); // Add class for print-specific body styles
    return () => {
      document.body.classList.remove('print-mode'); // Clean up on unmount
    };
  }, []);

  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) {
      setError('Необходимо указать корректный диапазон дат.');
      setLoading(false);
      setStructures([]);
      return;
    }

    if (!structureIdsParam) {
      setError('Не выбраны конструкции для печати. Пожалуйста, вернитесь и выберите конструкции.');
      setLoading(false);
      setStructures([]);
      return;
    }

    const selectedIds = new Set(structureIdsParam.split(',').map(Number));
    if (selectedIds.size === 0) {
      setError('Не выбраны корректные ID конструкций для печати.');
      setLoading(false);
      setStructures([]);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchParams: Record<string, string> = {
      startDate: formatDateFns(dateRange[0], 'yyyy-MM-dd'),
      endDate: formatDateFns(dateRange[1], 'yyyy-MM-dd'),
    };
    // We fetch all available for the period/type/search first, then filter by IDs client-side
    // This is because the /structures/available endpoint might not support filtering by a list of IDs directly
    // and we want to respect the original filters (type, search) as well.
    if (typeParam !== 'all') {
      fetchParams.type = typeParam;
    }
    if (searchTermParam) {
      fetchParams.search = searchTermParam;
    }

    axios.get<Structure[]>('/structures/available', { params: fetchParams })
      .then(response => {
        const allAvailableStructures = response.data;
        const filteredStructures = allAvailableStructures.filter(structure =>
          selectedIds.has(structure.id)
        );
        
        if (filteredStructures.length === 0) {
          setError('Выбранные конструкции не найдены среди доступных за указанный период или по заданным фильтрам.');
          setStructures([]);
        } else {
          setStructures(filteredStructures);
        }
      })
      .catch(err => {
        console.error('Error fetching available structures for print:', err);
        setError('Ошибка при загрузке данных для печати.');
        setStructures([]);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [startDateParam, endDateParam, typeParam, searchTermParam, structureIdsParam]); // Added structureIdsParam

  const handlePrint = () => {
    window.print();
  };

  const companyName = settings?.companyName || settings?.name || 'Ваша Компания';
  const companyDetails = settings?.address
    ? `${settings.address}${settings.phone ? `, тел: ${settings.phone}` : ''}`
    : 'Контактная информация';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
        <p className="ml-2">Загрузка данных для печати...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-600">
        <p>{error}</p>
        <Link to="/structures/availability" className="btn-secondary mt-4 inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к выбору свободки
        </Link>
      </div>
    );
  }

  return (
    <div className="printable-container-wrapper bg-gray-100 min-h-screen">
      <div className="p-4 bg-white shadow-md mb-4 flex justify-between items-center print:hidden">
        <h2 className="text-xl font-semibold">Печать отчета о свободных конструкциях</h2>
        <div>
          <Link
            to={`/structures/availability?startDate=${startDateParam || ''}&endDate=${endDateParam || ''}&type=${typeParam}&search=${searchTermParam}`}
            className="btn-secondary mr-2"
            title="Вернуться к выбору конструкций"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1 inline-block" />
            Назад
          </Link>
          <button
            onClick={handlePrint}
            className="btn-primary"
            disabled={structures.length === 0 || loading} // Disable if no structures to print or still loading
          >
            <PrinterIcon className="h-5 w-5 mr-1 inline-block" />
            Распечатать
          </button>
        </div>
      </div>
      <PrintableStructureAvailability
        structures={structures}
        dateRange={dateRange}
        selectedType={typeParam}
        searchTerm={searchTermParam}
        companyName={companyName}
        companyDetails={companyDetails}
      />
    </div>
  );
};

export default StructureAvailabilityPrintPage;